import { type FC } from "react";
import { Dialog, CircularProgress, Box } from "@mui/material";
import {
  useStreamQuery,
  useStreamRecordingTrimQuery,
  useContentStreamStatusChangedSubscription,
} from "@/partner/entities/streams";
import { useWsReconnected } from "@/shared/utils/wsReconnect";
import { RecordingTrimStatusType } from "@/types/graphql/partner.generated";
import type { StreamTab } from "../model";
import { StreamForm } from "./StreamForm";

const TRIM_POLL_INTERVAL_MS = 5000;

interface EditStreamDialogProps {
  open: boolean;
  contentId: string | null;
  tab: StreamTab;
  onClose: () => void;
}

// Подписка может пропустить событие (сокет переподключался), и превью
// залипало в «Нет сигнала» (Ring C, 24.10). Страховка: перезапрос стрима
// при переподключении сокета и раз в минуту, пока диалог открыт.
const STREAM_REFRESH_MS = 60_000;

export const EditStreamDialog: FC<EditStreamDialogProps> = ({
  open,
  contentId,
  tab,
  onClose,
}) => {
  const active = Boolean(contentId) && open;
  const { data, loading, refetch } = useStreamQuery({
    variables: { contentId: contentId ?? "" },
    skip: !active,
    pollInterval: active ? STREAM_REFRESH_MS : 0,
  });

  useContentStreamStatusChangedSubscription({
    variables: { id: contentId ?? "" },
    skip: !active,
  });

  useWsReconnected(() => {
    void refetch();
  }, active);

  const content = data?.content;
  const stream = content?.stream;

  // Прогресс обрезки записи (CUTTING/COPYING) подписка не присылает: пока
  // обрезка активна, опрашиваем лёгкий запрос, он пишет в тот же Stream
  // в кэше и не трогает playback основного запроса.
  const trimStatus = stream?.recordingTrim?.status;
  const trimActive =
    !!trimStatus &&
    trimStatus !== RecordingTrimStatusType.DoneType &&
    trimStatus !== RecordingTrimStatusType.FailedType;
  useStreamRecordingTrimQuery({
    variables: { contentId: contentId ?? "" },
    skip: !contentId || !open || !trimActive,
    pollInterval: TRIM_POLL_INTERVAL_MS,
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : content && stream ? (
        <StreamForm
          content={content}
          stream={stream}
          tab={tab}
          onClose={onClose}
        />
      ) : null}
    </Dialog>
  );
};
